/**
 * Pure, dependency-free cache-busting for brew-CDN asset URLs.
 *
 * Brand logos are uploaded to DETERMINISTIC, STABLE paths — e.g.
 * `https://cdn.brew.new/brand/<brandId>/logo-<i>.<ext>` — written with
 * Vercel Blob `put({ allowOverwrite: true })` and NO content hash or
 * random suffix (see `lib/core/blob/storage.ts`). So when a brand
 * is re-extracted, the bytes at that URL change but the URL itself does
 * not.
 *
 * Next.js's image optimizer caches its optimized output keyed by
 * `(url, width, quality)` and serves it with `Cache-Control:
 * max-age=31536000` (one year, `x-vercel-cache: HIT`). Because the URL
 * is stable, a logo swap is invisible to that cache for up to a year —
 * and a poisoned entry (an optimization taken while the path briefly
 * held the wrong image) can make a brand render a STALE or even a
 * DIFFERENT brand's logo. This is width-specific: only the exact widths
 * previously optimized are stuck, so a 22px switcher icon (requested at
 * `w=48`) can be wrong while a larger render of the same logo is fine.
 *
 * Appending `?v=<brand.updatedAt>` moves the version into the URL, so the
 * optimizer's cache key changes whenever the brand changes (every logo
 * write goes through `patchBrand`, which always bumps `updatedAt`). The
 * current, correct source is then re-optimized on next request.
 *
 * The brew CDN ignores the unknown `v` param and serves the same bytes,
 * so this is safe. We restrict rewriting to `cdn.brew.new` — third-party
 * hosts (e.g. Google s2 favicons) are overwrite-free and are left
 * untouched, since an extra query param there could be significant.
 */

/** Host of the brew asset CDN whose objects are overwritten in place. */
const BREW_ASSET_HOST = 'cdn.brew.new'

/**
 * Returns `src` with a `?v=<version>` cache-buster appended when `src` is
 * a `cdn.brew.new` asset URL and `version` is present; otherwise returns
 * `src` unchanged. Preserves any existing query string and never throws.
 *
 * @param src     Absolute asset URL (or undefined).
 * @param version A monotonic version for the asset — the owning brand's
 *                `updatedAt` (falls back to `_creationTime`). Floored to
 *                an integer; ignored when nullish.
 */
export function withAssetCacheBust(
  src: string | undefined,
  version: number | undefined
): string | undefined {
  if (!src || version == null || !Number.isFinite(version)) {
    return src
  }
  let host: string
  try {
    host = new URL(src).host
  } catch {
    // Relative or unparseable URL — nothing to safely version.
    return src
  }
  if (host !== BREW_ASSET_HOST) {
    return src
  }
  const sep = src.includes('?') ? '&' : '?'
  return `${src}${sep}v=${Math.floor(version)}`
}
